import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import Warning from "./Warning";

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        !event.target.closest(".dropdown") &&
        !event.target.closest(".dropdown-button")
      ) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsOpen(false);
      setDropdownOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const services = [
    { name: "Cash MIS", id: "NCM" },
    { name: "MCX Pack", id: "NMCX" },
    { name: "Trade Nexus", id: "NTN" },
    { name: "Wealth Wave", id: "NWW" },
    { name: "Super Sensex", id: "SSX" },
    { name: "Stock Option MIS Pack", id: "NSOM" },
    { name: "Index Option MIS Pack", id: "NIOM" },
    { name: "Commodity Pack (Base Metal & Energy)", id: "NCMP" },
  ];

  return (
    <nav className="bg-white text-green-700 font-bold tracking-wider font-amiriQuran shadow-lg z-50 sticky top-0">
      <div className="max-w-screen-xl px-4 sm:px-6 lg:px-8 py-3 mx-auto flex items-center justify-between font-AmiriQuran">
        {/* Logo */}
        <Link className="flex items-center" to={"/"}>
          <img
            src="/logo1.png"
            alt="Nextera Research Logo"
            width={150}
            className="cursor-pointer hover:opacity-90 transition-opacity duration-200  tracking-wider"
          />
        </Link>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center">
          <button
            onClick={() => setIsOpen(true)}
            className="p-2 rounded-md hover:bg-[#b0b0b0] transition-colors duration-200  tracking-wider"
            aria-label="Open menu"
          >
            <Menu size={28} className=" text-gray-800" />
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        {isOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-70 z-40"
            onClick={() => setIsOpen(false)}
          />
        )}

        {/* Mobile Sidebar Menu */}
        <motion.div
          initial={{ x: "-100%" }}
          animate={{ x: isOpen ? 0 : "-100%" }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="fixed top-0 left-0 h-full w-full bg-white shadow-lg p-6 z-50 overflow-y-auto"
        >
          {/* Close Button */}
          <button
            className="absolute top-4 right-4 text-gray-800"
            onClick={() => setIsOpen(false)}
          >
            <X size={24} />
          </button>

          <ul className="flex flex-col space-y-6 text-lg text-gray-800">
            <li className="border-b border-gray-200">
              <Link
                to="/"
                className="block py-3 hover:text-[#175f33]"
                onClick={() => setIsOpen(false)}
              >
                Home
              </Link>
            </li>
            <li className="border-b border-gray-200">
              <Link
                to="/aboutus"
                className="block py-3 hover:text-[#175f33]"
                onClick={() => setIsOpen(false)}
              >
                About us
              </Link>
            </li>

            <li className="border-b border-gray-200 py-3">
              {/* Features Dropdown */}
              <button
                className="flex items-center justify-between w-full hover:text-indigo-500 dropdown-button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <Link to="/services">Services </Link>
                <ChevronDown
                  size={18}
                  className={`${dropdownOpen ? "rotate-180" : ""
                    } transition-transform`}
                />
              </button>

              {dropdownOpen && (
                <motion.ul
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="pl-6 mt-2 space-y-4 w-full"
                >
                  {services.map((service) => (
                    <li key={service.name} className=' border-b'>
                      <Link
                        to={`/services/${service.id}`}
                        className="font-normal"
                        onClick={() => {
                          setDropdownOpen(false);
                          setIsOpen(false);
                        }}
                      >
                        {service.name}
                      </Link>
                    </li>
                  ))}
                </motion.ul>
              )}
            </li>

            <li className="border-b border-gray-200">
              <Link
                to="/pricing"
                className="block py-3 hover:text-[#175f33]"
                onClick={() => setIsOpen(false)}
              >
                Pricing
              </Link>
            </li>

            <li className="border-b border-gray-200">
              <Link
                to="/secure-payment"
                className="block py-3 hover:text-[#175f33]"
                onClick={() => setIsOpen(false)}
              >
                Payment
              </Link>
            </li>
            

            <li className="border-b border-gray-200">
              <Link
                to="/contactus"
                className="block py-3 hover:text-[#175f33]"
                onClick={() => setIsOpen(false)}
              >
                Contact Us
              </Link>
            </li>

            {/* Added  ATS  ( Advanced trading Solutions) to mobile menu */}
            <li className="border-b border-gray-200">
              <Link
                to="/service-api"
                className="block py-3 hover:text-[#175f33]"
                onClick={() => setIsOpen(false)}
              >
                 ATS  ( Advanced trading Solutions)
              </Link>
            </li>
               
          </ul>
          <div className="mt-10">
            <button
              className="
                w-full
                bg-white hover:bg-blue-50 
                text-green-600 font-medium 
                py-2 px-4 
                rounded-lg 
                border-2 border-green-200 
                transition-all duration-300 ease-in-out 
                hover:scale-[1.02] hover:shadow-md 
                active:scale-100
                text-center
              "
              onClick={() => {
                window.open("https://software.nexteraresearch.in/");
                setIsOpen(false);
              }}
            >
              Login
            </button>
          </div>
        </motion.div>

        {/* Desktop Menu */}
        <div className="hidden md:flex justify-between items-center space-x-1">

          <ul className="flex space-x-1 items-center">
            <li>
              <Link
                to="/"
                className="px-4 py-2 rounded-lg font-bold hover:bg-[#175f33] hover:text-white transition-colors duration-200 tracking-wider"
              >
                Home
              </Link>
            </li>
            <li>
              <Link
                to="/aboutus"
                className="px-4 py-2 rounded-lg font-bold hover:bg-[#175f33] hover:text-white transition-colors duration-200  tracking-wider"
              >
                About us
              </Link>
            </li>
            <li className="relative group">
              <div className="flex items-center">
                <Link
                  to="/services"
                  className="px-4 py-2 rounded-lg font-bold hover:bg-[#175f33] hover:text-white transition-colors duration-200  tracking-wider"
                >
                  Services
                </Link>
                <ChevronDown
                  size={18}
                  className="ml-1 group-hover:rotate-180 transition-transform duration-200  tracking-wider"
                />
              </div>

              <div className="absolute left-0 top-full mt-1 w-56 bg-white shadow-xl rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                {services.map((service) => (
                  <Link
                    key={service.name}
                    to={`/services/${service.id}`}
                    className="block px-4 py-2 text-[#0e4322] hover:bg-[#def3d4] transition-colors font-normal first:rounded-t-lg last:rounded-b-lg"
                  >
                    {service.name}
                  </Link>
                ))}
              </div>
            </li>
            <li>
              <Link
                to="/pricing"
                className="px-4 py-2 rounded-lg font-bold hover:bg-[#175f33] hover:text-white transition-colors duration-200  tracking-wider"
              >
                Pricing
              </Link>
            </li>
            <li>
              <Link
                to="/secure-payment"
                className="px-4 py-2 rounded-lg font-bold hover:bg-[#175f33] hover:text-white transition-colors duration-200  tracking-wider"
              >
                Payment
              </Link>
            </li>
        
            <li>
              <Link
                to="/contactus"
                className="px-4 py-2 rounded-lg font-bold hover:bg-[#175f33] hover:text-white transition-colors duration-200  tracking-wider"
              >
                Contact Us
              </Link>
            </li>
            <li>
              <Link
                to="/service-api"
                className="px-4 py-2 rounded-lg font-bold hover:bg-[#175f33] hover:text-white transition-colors duration-200  tracking-wider"
              >
                 ATS 
              </Link>
            </li>
               
          </ul>

        </div>
        <button
          className=" hidden md:flex
              bg-white hover:bg-blue-50 
              text-green-600 font-medium 
              py-2 px-4 
              rounded-lg
              border-2 border-green-200 
              transition-all duration-300 ease-in-out 
              hover:scale-105 hover:shadow-md 
              active:scale-100
            "
          onClick={() => window.open("https://software.nexteraresearch.in/")}
        >
          Login
        </button>
      </div>
      <Warning />
    </nav>
  );
}

export default Navbar;